import { useCallback, useMemo, useState } from 'react';

const filterOutRole = (roleUri, prev) =>
  prev.filter(r => r.roleUri !== roleUri);

const hasRole = (roles, roleUri) =>
  roles.some(role => role.roleUri === roleUri);

// The default as the dialog currently shows it: a pending default wins,
// otherwise the saved default unless the user already cleared or unassigned it.
const getCurrentDefaultUri = ({
  rolesToAssign,
  savedDefaultUri,
  roleUrisToUnassign
}) => {
  const pendingDefault = rolesToAssign.find(role => role.isDefault);

  if (pendingDefault) return pendingDefault.roleUri;

  if (!savedDefaultUri || roleUrisToUnassign.has(savedDefaultUri)) return null;

  // An explicit isDefault false entry means the saved default was cleared
  const isSavedDefaultCleared = rolesToAssign.some(
    role => role.roleUri === savedDefaultUri && !role.isDefault
  );

  return isSavedDefaultCleared ? null : savedDefaultUri;
};

// Keeps an explicit isDefault false entry, so a saved default is also cleared
// on the server when saving.
const removeDefaultFromRole = (rolesToAssign, clickedRole) => {
  const rolesWithoutDefault = rolesToAssign.map(role =>
    role.roleUri === clickedRole.roleUri ? { ...role, isDefault: false } : role
  );

  if (!hasRole(rolesWithoutDefault, clickedRole.roleUri)) {
    rolesWithoutDefault.push({
      roleUri: clickedRole.roleUri,
      displayText: clickedRole.displayText,
      isDefault: false,
      isNew: false
    });
  }

  return rolesWithoutDefault;
};

// Going back to the saved default means the server already has it, so only the
// newly added roles are left to send.
const restoreSavedDefault = rolesToAssign =>
  rolesToAssign
    .filter(role => role.isNew)
    .map(role => ({ ...role, isDefault: false }));

const makeRoleDefault = ({ rolesToAssign, clickedRole }) => {
  const rolesWithNewDefault = rolesToAssign.map(role => ({
    ...role,
    isDefault: role.roleUri === clickedRole.roleUri
  }));

  if (!hasRole(rolesWithNewDefault, clickedRole.roleUri)) {
    rolesWithNewDefault.push({
      roleUri: clickedRole.roleUri,
      displayText: clickedRole.displayText,
      isDefault: true,
      isNew: !clickedRole.isAssigned
    });
  }

  return rolesWithNewDefault;
};

export const buildRolesWithDefault = ({
  rolesToAssign,
  clickedRole,
  savedDefaultRole,
  roleUrisToUnassign
}) => {
  const savedDefaultUri = savedDefaultRole?.id ?? null;
  const currentDefaultUri = getCurrentDefaultUri({
    rolesToAssign,
    savedDefaultUri,
    roleUrisToUnassign
  });

  if (clickedRole.roleUri === currentDefaultUri) {
    return removeDefaultFromRole(rolesToAssign, clickedRole);
  }

  if (clickedRole.roleUri === savedDefaultUri) {
    return restoreSavedDefault(rolesToAssign);
  }

  return makeRoleDefault({ rolesToAssign, clickedRole });
};

const useSpecificRolesSelectorHandler = ({
  savedDefaultRole,
  savedRoleCount
}) => {
  const [rolesToAssign, setRolesToAssign] = useState([]);
  const [roleUrisToUnassign, setRoleUrisToUnassign] = useState(new Set());

  const reset = useCallback(() => {
    setRolesToAssign([]);
    setRoleUrisToUnassign(new Set());
  }, []);

  const isRoleChecked = useCallback(
    ({ roleUri, isAssigned }) => {
      if (roleUrisToUnassign.has(roleUri)) return false;

      if (isAssigned) return true;

      return rolesToAssign.some(r => r.roleUri === roleUri && r.isNew);
    },
    [rolesToAssign, roleUrisToUnassign]
  );

  const defaultRole = useMemo(() => {
    const pendingDefault = rolesToAssign.find(r => r.isDefault);

    if (pendingDefault) {
      return {
        id: pendingDefault.roleUri,
        displayText: pendingDefault.displayText
      };
    }

    const currentDefaultUri = getCurrentDefaultUri({
      rolesToAssign,
      savedDefaultUri: savedDefaultRole?.id ?? null,
      roleUrisToUnassign
    });

    return currentDefaultUri ? savedDefaultRole : null;
  }, [rolesToAssign, roleUrisToUnassign, savedDefaultRole]);

  const selectedCount = useMemo(
    () =>
      Math.max(
        0,
        (savedRoleCount ?? 0) +
          rolesToAssign.filter(r => r.isNew).length -
          roleUrisToUnassign.size
      ),
    [savedRoleCount, rolesToAssign, roleUrisToUnassign]
  );

  const removeFromUnassign = useCallback(roleUri => {
    setRoleUrisToUnassign(prev => {
      const next = new Set(prev);

      next.delete(roleUri);

      return next;
    });
  }, []);

  const addToUnassign = useCallback(roleUri => {
    setRoleUrisToUnassign(prev => new Set([...prev, roleUri]));
  }, []);

  const addToAssign = useCallback((roleUri, displayText) => {
    setRolesToAssign(prev => [
      ...prev,
      { roleUri, displayText, isDefault: false, isNew: true }
    ]);
  }, []);

  const removeFromAssign = useCallback(roleUri => {
    setRolesToAssign(prev => filterOutRole(roleUri, prev));
  }, []);

  const handleToggle = useCallback(
    ({ roleUri, displayText, isAssigned, isCurrentlyChecked }) => {
      if (isCurrentlyChecked) {
        if (isAssigned) addToUnassign(roleUri);
        removeFromAssign(roleUri);
      } else if (isAssigned) {
        removeFromUnassign(roleUri);
      } else {
        addToAssign(roleUri, displayText);
      }
    },
    [removeFromUnassign, addToUnassign, addToAssign, removeFromAssign]
  );

  const handleSetDefault = useCallback(
    ({ roleUri, displayText, isAssigned }) => {
      setRolesToAssign(currentRolesToAssign =>
        buildRolesWithDefault({
          rolesToAssign: currentRolesToAssign,
          clickedRole: { roleUri, displayText, isAssigned },
          savedDefaultRole,
          roleUrisToUnassign
        })
      );
    },
    [savedDefaultRole, roleUrisToUnassign]
  );

  return {
    rolesToAssign,
    roleUrisToUnassign,
    isRoleChecked,
    defaultRole,
    selectedCount,
    handleToggle,
    handleSetDefault,
    reset
  };
};

export default useSpecificRolesSelectorHandler;
